// Overlapping interiors (not merely shared boundaries) usually mean the same ground is declared twice.
import * as turf from '@turf/turf'

const MIN_OVERLAP_SQUARE_METERS = 10 // ignore boundary slivers between honest neighbors

// Not part of GeometryWarning (analyzeFeature is per-feature and can't see the collection); non-blocking.
export const OVERLAP_WARNING = 'overlapping'

// Both memos are keyed by geometry identity (see the memo note in EudrGeometry.js): an edit replaces one geometry
// object, so only the pairs involving that geometry are intersected again instead of every touching pair.
const bboxMemo = new WeakMap()
const overlapAreaMemo = new WeakMap()

function geometryBbox (geometry) {
  if (bboxMemo.has(geometry)) return bboxMemo.get(geometry)
  let bbox
  try {
    bbox = turf.bbox(geometry)
  } catch (e) {
    bbox = null
  }
  bboxMemo.set(geometry, bbox)
  return bbox
}

function bboxesTouch (a, b) {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
}

function overlapArea (a, b) {
  const cached = overlapAreaMemo.get(a)?.get(b)
  if (cached !== undefined) return cached
  let area
  try {
    const intersection = turf.intersect(turf.featureCollection([turf.feature(a), turf.feature(b)]))
    area = intersection ? turf.area(intersection) : 0
  } catch (e) {
    area = 0 // invalid geometry is reported by the §6 checks, not here
  }
  for (const [from, to] of [[a, b], [b, a]]) {
    if (!overlapAreaMemo.has(from)) overlapAreaMemo.set(from, new WeakMap())
    overlapAreaMemo.get(from).set(to, area)
  }
  return area
}

// Returns { overlappingCount, overlappingIndices } — featureCollection indices, so callers can badge the shapes.
export function findOverlaps (featureCollection) {
  const polygonal = (featureCollection?.features ?? [])
    .map((feature, index) => ({ feature, index }))
    .filter(entry => ['Polygon', 'MultiPolygon'].includes(entry.feature.geometry?.type))
    .map(entry => ({ ...entry, bbox: geometryBbox(entry.feature.geometry) }))
    .filter(entry => entry.bbox)

  const overlapping = new Set()
  for (let i = 0; i < polygonal.length; i++) {
    for (let j = i + 1; j < polygonal.length; j++) {
      if (!bboxesTouch(polygonal[i].bbox, polygonal[j].bbox)) continue
      if (overlapArea(polygonal[i].feature.geometry, polygonal[j].feature.geometry) > MIN_OVERLAP_SQUARE_METERS) {
        overlapping.add(polygonal[i].index)
        overlapping.add(polygonal[j].index)
      }
    }
  }
  return { overlappingCount: overlapping.size, overlappingIndices: [...overlapping] }
}
